'use client'

import cx from 'classnames'
import { useEffect, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { SwiperSlide } from 'swiper/react'

import { AnimationChildrenProps } from '@/components/LandingPage/ContentModel/Animation'
import Accordion from '@/components/LandingPage/ContentModel/Box/Accordion'
import BrowserBack from '@/components/LandingPage/ContentModel/Box/BrowserBack'
import Dialog from '@/components/LandingPage/ContentModel/Box/Dialog'
import ContentModelLogic from '@/components/LandingPage/ContentModel/ContentModelLogic'
import ContentModels from '@/components/LandingPage/ContentModels'
import { Props } from '@/components/Optimizer/ClientOptimizer'
import Slider from '@/components/Slider'
import { CampaignType } from '@/types/Campaign'
import { ContentModelBoxType } from '@/types/ContentModel/ContentModelBox'
import { ProductType } from '@/types/product'

export default function Box({
  contentModel,
  animationRef = null,
  style = {},
  className = '',
  product,
  campaign,
  optimizer,
  firstView,
  afterSubmit,
}: {
  contentModel: ContentModelBoxType
  optimizer: Props
  firstView: boolean
  product: ProductType | null
  campaign: CampaignType | null
  afterSubmit: (() => void) | null
} & AnimationChildrenProps) {
  const contentModels = (
    <ContentModels
      contentModels={contentModel.contentModels}
      baseline={contentModel.layout == 'baseline'}
      product={product}
      campaign={campaign}
      optimizer={optimizer}
      firstView={firstView}
      afterSubmit={afterSubmit}
    />
  )

  const [show, setShow] = useState(contentModel.fixedButton?.timing != 'inView')

  const { inView, ref } = useInView({
    triggerOnce: true,
  })

  useEffect(() => {
    if (!show && inView) {
      setShow(true)
    }
  }, [inView])

  return (
    <>
      <div ref={ref} />
      <div
        className={className}
        {...(animationRef != null ? { ref: animationRef } : {})}
        {...(contentModel.id ? { id: contentModel.id } : {})}
        style={{
          ...style,
          ...(contentModel.layout === 'vertical'
            ? {
                display: 'flex',
                flexDirection: 'column',
                ...(contentModel.justifyContent
                  ? {
                      justifyContent: contentModel.justifyContent,
                    }
                  : {}),
                ...(contentModel.alignItems
                  ? {
                      alignItems: contentModel.alignItems,
                    }
                  : {}),
                ...(contentModel.height
                  ? {
                      height: contentModel.height,
                    }
                  : {}),
              }
            : {}),
          ...(contentModel.layout === 'horizon'
            ? {
                display: 'flex',
                flexDirection: 'row',
                ...(contentModel.justifyContent
                  ? {
                      justifyContent: contentModel.justifyContent,
                    }
                  : {}),
                ...(contentModel.alignItems
                  ? {
                      alignItems: contentModel.alignItems,
                    }
                  : {}),
                ...(contentModel.height
                  ? {
                      height: contentModel.height,
                    }
                  : {}),
              }
            : {}),
          ...(contentModel.backgroundImage
            ? {
                position: 'relative',
              }
            : {}),
          ...(contentModel.fixedButton?.timing == 'inView'
            ? {
                position: 'fixed',
                right: '2rem',
                transition: 'bottom 0.2s',
                bottom: show ? '2rem' : '-6rem',
                zIndex: 999,
              }
            : {}),
        }}
      >
        {contentModel.carousel ? (
          <Slider
            items={contentModel.contentModels.map((childContentModel, index) => {
              return (
                <SwiperSlide
                  className={cx('overflow-hidden', {
                    'border-l': contentModel.carousel == true,
                    'border-[#fff]': contentModel.carousel == true,
                  })}
                  key={index}
                >
                  <ContentModelLogic
                    product={product}
                    campaign={campaign}
                    contentModel={childContentModel}
                    optimizer={optimizer}
                    firstView={firstView ? index == 0 : false}
                  />
                </SwiperSlide>
              )
            })}
            carouselProps={
              contentModel.carousel == true
                ? {
                    autoplayDelay: 0,
                    slidesPerView: 1.2,
                    speed: 1000 * 15,
                  }
                : contentModel.carousel
            }
          />
        ) : contentModel.accordion ? (
          <Accordion title={contentModel.accordion.title} initOpen={contentModel.accordion.open == true}>
            {contentModels}
          </Accordion>
        ) : contentModel.modal ? (
          <Dialog
            trigger={contentModel.modal.trigger || 'inView'}
            id={contentModel.modal.id || 'identifier'}
            {...{
              ...(contentModel.modal.rootMargin ? { rootMargin: contentModel.modal.rootMargin } : {}),
              ...(contentModel.modal.threshold ? { threshold: contentModel.modal.threshold } : {}),
            }}
          >
            {contentModels}
          </Dialog>
        ) : contentModel.browserBack?.measurementId ? (
          <BrowserBack measurementId={contentModel.browserBack?.measurementId}>{contentModels}</BrowserBack>
        ) : (
          <>{contentModels}</>
        )}
      </div>
    </>
  )
}
